import React, { useEffect, useState, useCallback } from "react";
import TemplateApi from "../../api/TemplateApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import Auth from "../../utils/AuthUtils";
import { Navigate } from "react-router-dom";
import { can } from "../../utils/permissionUtils";

function List() {

  const [soldTemplates, setSoldTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [targetId, setTargetId] = useState(null);
  const [restoreReason, setRestoreReason] = useState("");
  const [restoring, setRestoring] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");


  const user = Auth.getUserDetails();
  const permissions = user?.permissions;

  const loadSoldData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await TemplateApi.soldList();
      if (res.code === 200) {
        setSoldTemplates(res.data);
      } else {
        showError(res.message || "Unable to fetch data");
      }
    } catch (err) {
      showError("Connection to server failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (can(permissions, "read", "Sell Templates")) {
      loadSoldData();
    } else {
      setLoading(false);
    }
  }, []); 

  const handleMoveToInventory = async (e) => {
    e.preventDefault();
    if (!targetId) return;
    if (!restoreReason.trim()) return showError("Please provide a reason for restoration");

    try {
      setRestoring(true);
      const res = await TemplateApi.removeFromSell({ 
        templateId: targetId,
        reason: restoreReason 
      });

      if (res.code === 200) {
        showSuccess("Template successfully restored to inventory");
        loadSoldData();
        setTargetId(null);
        setRestoreReason("");
        
        const modalElement = document.getElementById("confirmReturnModal");
        if (window.bootstrap) {
          const modalInstance = window.bootstrap.Modal.getInstance(modalElement) || new window.bootstrap.Modal(modalElement);
          modalInstance.hide();
        }
      }
    } catch (err) {
      showError(err.response?.data?.message || "Action failed");
    } finally {
      setRestoring(false);
    }
  };


  const filteredsoldTemplates = soldTemplates.filter((item) => {
  const search = searchTerm.toLowerCase().trim();

  return item.soldTemplates?.toLowerCase().includes(search);
});


  return (
    <div className="content pb-0">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-bold">
          Sold History
          <span className="badge badge-soft-danger ms-2">{soldTemplates.length}</span>
        </h4>
      </div>

       <div className="group-search-box mb-4 position-relative">
          <i className="ti ti-search search-icon"></i>

          <input
            type="text"
            className="form-control search-input"
            placeholder="Search by Sold Template..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

      {loading ? (
        <div className="p-5 text-center">
          <div className="spinner-border text-danger mb-2"></div>
          <p className="text-muted">Loading Records...</p>
        </div>
      ) : (
        <div className="row">
          {soldTemplates.length === 0 ? (
            <div className="col-12 text-center p-5">
              <i className="ti ti-package-off fs-1 text-muted mb-2"></i>
              <h5 className="text-muted">No Sold Templates Found</h5>
            </div>
          ) : (
            soldTemplates.map((item) => (
              <div className="col-md-4 mb-4" key={item._id}>
                <div className="card h-100 border-0 shadow-sm overflow-hidden card-hover-shadow">
                  <div className="card-img-top bg-light position-relative" style={{ height: "180px" }}>
                    <iframe
                    src={item.url}
                    title={item.templateName}
                    width="100%"
                    height="100%"
                    style={{ 
                      border: "none", 
                       pointerEvents: "none", 
                      zoom: "0.5", // Scale preview to fit
                      WebkitTransform: "scale(1)",
                      transformOrigin: "0 0"
                    }}
                    sandbox="allow-scripts allow-same-origin"
                    loading="lazy"
                  ></iframe>
                    <span className="position-absolute top-0 start-0 m-2 badge bg-danger shadow-sm">SOLD</span>
                  </div>

                  <div className="card-body">
                    <h6 className="fw-bold text-dark text-truncate mb-1">{item.templateName}</h6>
                    <div className="mb-2 p-2 bg-light rounded-2 border-start border-primary border-3">
                        <small className="text-muted d-block">Customer:</small>
                        <span className="fw-semibold text-primary">
                           <i className="ti ti-user me-1"></i>
                           {item.selectedBy?.name || "Unknown Client"}
                        </span>
                        <small className="d-block text-truncate text-muted" style={{fontSize: '11px'}}>
                           {item.selectedBy?.email}
                        </small>
                    </div>
                    <p className="small text-muted text-truncate-2 mb-3">{item.description}</p>
                    <div className="d-flex gap-2">
                      <a href={item.url} target="_blank" rel="noreferrer" className="btn btn-sm btn-soft-info flex-grow-1">Preview</a>
                      {can(permissions, "delete", "sellTemplate") && (
                        <button className="btn btn-sm btn-soft-warning flex-grow-1" data-bs-toggle="modal" data-bs-target="#confirmReturnModal" onClick={() => setTargetId(item._id)}>Restore</button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <div className="modal fade" id="confirmReturnModal" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg">
            <div className="modal-header border-bottom-0">
               <h5 className="fw-bold">Restore to Inventory</h5>
               <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <form onSubmit={handleMoveToInventory}>
              <div className="modal-body py-0">
                <p className="small text-muted mb-3">Please provide a reason for restoration.</p>
                <div className="mb-3">
                  <label className="form-label fw-semibold small text-uppercase ls-1">Restoration Reason <span className="text-danger">*</span></label>
                  <textarea className="form-control" rows="3" placeholder="Reason..." value={restoreReason} onChange={(e) => setRestoreReason(e.target.value)} required></textarea>
                </div>
              </div>
              <div className="modal-footer border-top-0">
                <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                <button type="submit" className="btn btn-warning" disabled={restoring}>{restoring ? "Restoring..." : "Confirm Restore"}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default List;